import { useEffect, useRef, useState } from "react";
import { Button } from "@aws-amplify/ui-react";
import { UITypography } from "../../../_app/components";
import UIInputOtp from "../../../_app/components/UIInputOtp";
import AuthFormShell from "./AuthFormShell";

const OTP_LENGTH = 6;

interface ConfirmTotpFormProps {
  // Returns whether the code was accepted, so the form can clear + refocus on a wrong code.
  onSubmit: (code: string) => Promise<boolean>;
  onBack: () => void;
  onDismissError?: () => void;
  error?: string | null;
  isLoading?: boolean;
}

const ConfirmTotpForm = ({ onSubmit, onBack, onDismissError, error, isLoading }: ConfirmTotpFormProps) => {
  const [code, setCode] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const submittingRef = useRef(false);

  // Focus the first digit on mount and again after a failed attempt clears the inputs.
  useEffect(() => {
    containerRef.current?.querySelector("input")?.focus();
  }, [resetKey]);

  const submit = async (value: string) => {
    // Guard against the auto-submit (onCompleted) and the Confirm button / Enter firing twice
    // before the async isLoading flag has propagated.
    if (submittingRef.current || value.length !== OTP_LENGTH) return;
    submittingRef.current = true;
    try {
      const accepted = await onSubmit(value);
      if (!accepted) {
        setCode("");
        setResetKey((key) => key + 1); // remount UIInputOtp to clear the boxes
      }
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <AuthFormShell
      heading="Two-factor authentication"
      onSubmit={(event) => {
        event.preventDefault();
        submit(code);
      }}
      error={error}
      onDismissError={onDismissError}
      fields={
        <>
          <UITypography variant="body2" textAlign="center" color="text.secondary">
            Enter the 6-digit code from your authenticator app.
          </UITypography>
          <UIInputOtp
            key={resetKey}
            length={OTP_LENGTH}
            containerProps={{ ref: containerRef }}
            disabled={isLoading}
            error={Boolean(error)}
            onChange={(chars) => setCode(chars.map((char) => char.value).join(""))}
            onCompleted={(value) => submit(value)}
          />
        </>
      }
      actions={
        <>
          <Button type="submit" variation="primary" isFullWidth isLoading={isLoading} disabled={code.length !== OTP_LENGTH}>
            Confirm
          </Button>
          <Button type="button" variation="link" onClick={onBack} className="amplify-button--link">
            Back to Sign In
          </Button>
        </>
      }
    />
  );
};

export default ConfirmTotpForm;
